/**
 * Mappers — Bridge between @kontent-ai/delivery-sdk types and the legacy
 * IKT* types used throughout the codebase.
 *
 * These helpers allow resolvers migrated to the SDK to continue feeding
 * `resolveRichText()` and downstream components without changes.
 */

import type { Elements, IContentItem } from "@kontent-ai/delivery-sdk"

import type { Button as SDKButton } from "@/types/kontent-generated/types/button.generated"
import type { TextMedia as SDKTextMedia } from "@/types/kontent-generated/types/text-media.generated"
import type {
  IKTImage,
  IKTRichText,
  IKTRichTextComponent,
  IKTSEO,
} from "@/types/kontent.types"
import type { ResolvedApplicationTreeItem } from "@/lib/kontent/config/getApplicationTree"
import { resolveRichText } from "@/lib/kontent/richText"
import type { TButton } from "@/components/ui/atoms"

import type { TResolvedTextMedia } from "./components/getTextMedia"

// ---------------------------------------------------------------------------
// Rich Text mapping
// ---------------------------------------------------------------------------

/**
 * Converts an SDK `Elements.RichTextElement` into the legacy `IKTRichText`
 * shape expected by `resolveRichText()`.
 *
 * The SDK resolves linked items as full `IContentItem` objects while the
 * GraphQL layer returned a lighter structure with `__typename` and `_system_`.
 * This mapper bridges the gap so `resolveRichText()` can keep working as-is.
 */
export function mapRichTextElementToIKTRichText(
  element: Elements.RichTextElement<IContentItem>
): IKTRichText {
  // --- linkedItems ---
  const linkedItems: IKTRichText["linkedItems"] = element.linkedItems?.length
    ? {
        items: element.linkedItems.map(
          (item): IKTRichTextComponent => ({
            // The SDK type codename uses snake_case (e.g. "text_media").
            // The GraphQL __typename used PascalCase (e.g. "TextMedia").
            // `resolveRichText()` switches on PascalCase → convert.
            __typename: snakeToPascal(item.system.type),
            _system_: {
              id: item.system.id,
              codename: item.system.codename,
              workflowStep: {
                _system_: {
                  codename: item.system.workflowStep ?? "",
                },
              },
            },
            // Preserved so `resolveRichText()` can map it directly instead
            // of re-fetching by codename when the depth budget already
            // resolved it.
            rawItem: item,
          })
        ),
      }
    : undefined

  // --- itemHyperlinks (content item links) ---
  const itemHyperlinks: IKTRichText["itemHyperlinks"] = element.links?.length
    ? {
        items: element.links.map((link) => ({
          _system_: {
            id: link.linkId,
            codename: link.codename,
            path: "", // resolved later by resolveRichText → getWebSpotlightApplicationTree
          },
        })),
      }
    : undefined

  // --- assets (inline images in rich text) ---
  const assets: IKTRichText["assets"] = element.images?.length
    ? {
        items: element.images.map(
          (img): IKTImage => ({
            imageId: img.imageId,
            url: img.url,
            width: img.width ?? 0,
            height: img.height ?? 0,
            description: img.description ?? "",
            type: "",
          })
        ),
      }
    : undefined

  return {
    html: element.value,
    linkedItems,
    itemHyperlinks,
    assets,
  }
}

// ---------------------------------------------------------------------------
// Asset mapping
// ---------------------------------------------------------------------------

/**
 * Converts an SDK `AssetModel` into the legacy `IKTImage` shape used by
 * components like `HeroSectionResolver`.
 */
export function mapAssetToIKTImage(
  asset: Elements.AssetsElement["value"][number] | undefined
): IKTImage {
  if (!asset) {
    return {} as IKTImage
  }
  return {
    imageId: asset.url,
    url: asset.url,
    width: asset.width ?? 0,
    height: asset.height ?? 0,
    description: asset.description ?? "",
    type: asset.type ?? "",
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts a `snake_case` string to `PascalCase`.
 *
 * Examples:
 *   "text_media"        → "TextMedia"
 *   "highlighted_content" → "HighlightedContent"
 *   "button"            → "Button"
 */
export function snakeToPascal(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("")
}

// ---------------------------------------------------------------------------
// Custom element JSON value parsing
// ---------------------------------------------------------------------------

/**
 * Safely parses a custom element value (JSON string) and extracts `.value`.
 * Returns `null` when the input is empty/invalid.
 */
export function parseCustomElementJsonValue(
  raw: string | null | undefined
): string | null {
  if (!raw) return null
  try {
    return JSON.parse(raw)?.value ?? null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Button mapping
// ---------------------------------------------------------------------------

/**
 * Converts an SDK `Button` content item into the `TButton` shape consumed
 * by React components.
 *
 * The optional `byCodename` map is used to resolve linked pages to their
 * application tree entries (paths, breadcrumbs, etc.).
 */
export function mapButtonToTButton(
  button: SDKButton,
  byCodename?: Record<string, ResolvedApplicationTreeItem>
): TButton {
  const linkedPageItem = button.elements.linked_page.linkedItems[0]
  const linkedPage =
    linkedPageItem && byCodename
      ? byCodename[linkedPageItem.system.codename]
      : undefined

  return {
    codename: button.system.codename,
    ctaLabel: button.elements.cta_label.value,
    ctaLink: button.elements.cta_link.value,
    linkedPage,
    iconRight: parseCustomElementJsonValue(button.elements.icon_right.value),
    iconLeft: parseCustomElementJsonValue(button.elements.icon_left.value),
    style: button.elements.style.value[0]?.name ?? "",
  }
}

// ---------------------------------------------------------------------------
// Alert mapping
// ---------------------------------------------------------------------------

/**
 * Resolved alert shape shared across all page resolvers.
 */
export type TResolvedAlert = {
  icon?: string | null
  richText?: IKTRichText
}

/**
 * Extracts the `IKTRichText` from an SDK Alert linked-items element.
 * Returns `undefined` when there is no alert.
 */
export function mapAlertToIKTRichText(
  alertElement: Elements.LinkedItemsElement<IContentItem> | undefined
): IKTRichText | undefined {
  const alertItem = alertElement?.linkedItems?.[0]
  if (!alertItem) return undefined
  const alertMsg = alertItem.elements.alert_message
  if (!alertMsg?.value) return undefined
  return mapRichTextElementToIKTRichText(
    alertMsg as Elements.RichTextElement<IContentItem>
  )
}

/**
 * Maps a page's `alert_message` linked-items element into a `TResolvedAlert`.
 * Returns `undefined` when no alert is linked.
 */
export function mapPageAlertElement(elements: {
  alert_message?: Elements.LinkedItemsElement<IContentItem>
}): TResolvedAlert | undefined {
  const alertLinkedItem = elements.alert_message?.linkedItems?.[0]
  if (!alertLinkedItem) return undefined
  return {
    icon: parseCustomElementJsonValue(alertLinkedItem.elements.icon?.value),
    richText: mapAlertToIKTRichText(
      elements.alert_message as Elements.LinkedItemsElement<IContentItem>
    ),
  }
}

/**
 * Resolves the rich text within a `TResolvedAlert` (fetches linked items, etc.).
 * Returns the alert with its `richText` fully resolved, or `undefined` if no alert.
 */
export async function resolveAlert(
  alert: TResolvedAlert | undefined,
  previewMode: boolean
): Promise<TResolvedAlert | undefined> {
  if (!alert?.richText) return alert
  return {
    ...alert,
    richText: await resolveRichText(alert.richText, previewMode),
  }
}

// ---------------------------------------------------------------------------
// SEO mapping
// ---------------------------------------------------------------------------

/**
 * Converts SDK SEO snippet elements (prefixed with `seo__`) into the legacy
 * `IKTSEO` shape consumed by page components and meta tags.
 */
export type TSeoElements = {
  seo__title?: Elements.TextElement
  seo__meta_description?: Elements.TextElement
  seo__meta_keywords?: Elements.TextElement
  seo__url_canonical?: Elements.TextElement
  seo__og_title?: Elements.TextElement
  seo__og_description?: Elements.TextElement
  seo__og_image?: Elements.AssetsElement
  seo__robots_tags?: Elements.MultipleChoiceElement
}

export function mapSeoToIKTSEO(elements: TSeoElements): IKTSEO {
  return {
    title: elements.seo__title?.value ?? undefined,
    metaDescription: elements.seo__meta_description?.value ?? undefined,
    metaKeywords: elements.seo__meta_keywords?.value ?? undefined,
    urlCanonical: elements.seo__url_canonical?.value ?? undefined,
    ogTitle: elements.seo__og_title?.value ?? undefined,
    ogDescription: elements.seo__og_description?.value ?? undefined,
    ogImage: {
      items: (elements.seo__og_image?.value ?? []).map((asset) => ({
        url: asset.url ?? "",
        description: asset.description ?? "",
        height: asset.height ?? 0,
        name: asset.name ?? "",
        size: asset.size ?? 0,
        type: asset.type ?? "",
        width: asset.width ?? 0,
      })),
    },
    robotsTags: {
      items: (elements.seo__robots_tags?.value ?? []).map((choice) => ({
        _system_: {
          codename: choice.codename ?? "",
          name: choice.name ?? "",
        },
      })),
    },
  }
}

// ---------------------------------------------------------------------------
// TextMedia mapping (shared by getAnnuaires, getPartners, etc.)
// ---------------------------------------------------------------------------

/**
 * Converts an SDK `TextMedia` linked item into the `TResolvedTextMedia`
 * shape used by page resolvers.
 */
export function mapTextMediaToTResolvedTextMedia(
  tm: SDKTextMedia
): TResolvedTextMedia {
  const image = tm.elements.image.value[0]
  return {
    title: tm.elements.title.value,
    image: image ? mapAssetToIKTImage(image) : undefined,
    tag: tm.elements.tag.value,
    imagePosition: tm.elements.image_position.value[0]?.name ?? "",
    text: mapRichTextElementToIKTRichText(
      tm.elements.text as Elements.RichTextElement<IContentItem>
    ),
    icon: parseCustomElementJsonValue(tm.elements.icon.value) ?? "",
    textBackground: tm.elements.text_background.value[0]?.name ?? "",
    style: tm.elements.style.value[0]?.name ?? "",
  }
}
